import { useState } from 'react';
import { validateValues, type Metafield, type MetafieldValue } from '@forecastify/shared';
import { Button } from '@forecastify/ui';
import { useT } from '../../i18n';
import { MetafieldForm } from '../metafields/metafield-form';
import { SectionHeader } from './detail-chrome';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C30 · A workspace's own fields, on a contract or a line item.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP PUTS METAFIELDS ON THE TWO DIALOGS AND ON NEITHER DETAIL, AND THAT IS A GAP
 * RATHER THAN A DESIGN.** `2fazis.markup.html` 4341 and 4378 both carry a `MetaFieldsSection`; the
 * contract and line-item detail components carry none. A field you can only fill in at creation is
 * a field nobody can correct — and every other entity in this product (task, project, client,
 * person, worklog) lets its detail edit them. Flagged as an addition under CLAUDE.md rule 8 rather
 * than transcribed as drawn.
 *
 * ⚠️ **Renders nothing when the workspace has defined no fields for the entity**, which is most of
 * them. An empty card titled *Metafieldek* is a promise of configuration nobody made.
 *
 * ⚠️ **The draft commits on SAVE.** A metafield form is a form: a `PATCH` per keystroke would be a
 * request per character, and a required field would be refused while somebody typed into it.
 */
export function MetafieldsCard({
  fields,
  values,
  members,
  canEdit,
  onSave,
}: {
  /** The workspace's fields for THIS entity. Empty is the common case. */
  readonly fields: readonly Metafield[];
  readonly values: Readonly<Record<string, unknown>>;
  /** For the `user` type — the form picks a member from this. */
  readonly members: readonly { readonly id: string; readonly name: string }[];
  readonly canEdit: boolean;
  readonly onSave: (values: Record<string, unknown>) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);

  if (fields.length === 0) return null;

  const current = draft ?? values;
  const specs = fields.map((field) => ({
    key: field.key,
    name: field.name,
    type: field.type,
    required: field.required,
    options: field.options,
  }));

  /**
   * ⚠️ **Validated with the SERVER'S OWN function, before sending** — so a refusal appears beside
   * the input that caused it rather than arriving as a 400. Nine types across seven entities is
   * sixty-three chances for two implementations to disagree.
   *
   * ⚠️ Shown only once something has been typed: an untouched form is not "invalid", and a required
   * field flagged on arrival makes a new record look broken.
   */
  const errors = draft === null ? {} : validateValues(specs, draft).errors;

  return (
    <section>
      <SectionHeader title={t('contract.metafields')} />

      <div className="bg-card mt-1.5 rounded-lg border p-3">
        <MetafieldForm
          fields={[...fields]}
          values={current}
          errors={errors}
          members={[...members]}
          disabled={!canEdit}
          onChange={(key, value: MetafieldValue) => {
            setDraft({ ...current, [key]: value });
          }}
        />

        {draft === null ? null : (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              /* ⚠️ Disabled while anything is refused, so the round trip is not spent learning
                 what the local check already knows. */
              disabled={Object.keys(errors).length > 0}
              onClick={() => {
                onSave(draft);
                setDraft(null);
              }}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
